import React, { useEffect, useMemo, useState } from 'react';
import { DivIcon, LatLng } from 'leaflet';
import { Circle, MapContainer, Marker, TileLayer, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, LocateFixed } from 'lucide-react';
import { formatDate } from 'v3/utils/controlTowerDate';
import { platesLabel } from './contract';
import { detailText, vehicleLocation } from './detailsContract';

const TRUCK =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>';
const FLAG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/></svg>';
const pin = (className, html, size) =>
  new DivIcon({
    className: `tc-c-pin ${className}`,
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    tooltipAnchor: [size / 2, 0],
  });
const icons = {
  pickup: pin('tc-c-pin-pickup', FLAG, 34),
  vehicle: pin('tc-c-pin-vehicle', TRUCK, 28),
  refused: pin('tc-c-pin-vehicle tc-c-pin-refused', TRUCK, 28),
  incomplete: pin('tc-c-pin-vehicle tc-c-pin-incomplete', TRUCK, 28),
  selected: pin('tc-c-pin-vehicle tc-c-pin-selected', TRUCK, 40),
};
const coordinate = (value, limit) =>
  typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= limit;
const point = (value) =>
  value && coordinate(value.lat, 90) && coordinate(value.lng, 180) ? [value.lat, value.lng] : null;
/** Área inicial: o círculo do raio em volta da coleta e todos os veículos da lista. */
const overviewBounds = (pickup, radiusKm, points) =>
  points.reduce(
    (bounds, p) => bounds.extend([p.lat, p.lng]),
    new LatLng(pickup[0], pickup[1]).toBounds(Math.max(radiusKm, 1) * 2000),
  );

function MapControls({ pickup, radiusKm, points, selected }) {
  const map = useMap();
  useEffect(() => {
    const element = map.getContainer();
    const resize = new ResizeObserver(() => map.invalidateSize({ pan: false }));
    resize.observe(element);
    const zoom = map.zoomControl.getContainer();
    ['Aumentar zoom', 'Diminuir zoom'].forEach((label, index) => {
      const button = zoom?.children[index];
      button?.setAttribute('title', label);
      button?.setAttribute('aria-label', label);
    });
    return () => resize.disconnect();
  }, [map]);
  const overview = () => {
    if (pickup)
      map.fitBounds(overviewBounds(pickup, radiusKm, points), {
        animate: false,
        padding: [24, 24],
      });
  };
  const selectedKey = selected ? selected.join(',') : '';
  // Sem seleção: enquadra o raio. Com seleção: centraliza no veículo e aproxima.
  useEffect(() => {
    if (selected) map.flyTo(selected, 13, { duration: 0.6 });
    else overview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedKey, pickup?.join(','), radiusKm, points]);
  return (
    <button
      type="button"
      className="tc-c-recenter"
      title={selected ? 'Centralizar no veículo' : 'Centralizar na coleta'}
      aria-label={selected ? 'Centralizar no veículo' : 'Centralizar na coleta'}
      onClick={() => (selected ? map.setView(selected, 13, { animate: false }) : overview())}
    >
      <LocateFixed size={18} aria-hidden="true" />
    </button>
  );
}

function Caption({ location, overview, points }) {
  if (location.state === 'VALID') {
    const { position, vehicle } = location;
    return (
      <div className="tc-c-map-caption" role="status">
        <strong>Veículo {detailText(vehicle.plate)}</strong>
        <span>Posição: {formatDate(position.capturedAt)} · Brasília</span>
        <span>
          {[position.city, position.province].filter((s) => typeof s === 'string' && s).join('/') ||
            'Localidade não informada'}
        </span>
        <small>
          Latitude {position.lat} · Longitude {position.lng}
        </small>
        {position.validity === 'RECENT_24H' && (
          <small>
            Localização capturada nas últimas 24 horas · válida até{' '}
            {formatDate(position.validUntil)}
          </small>
        )}
      </div>
    );
  }
  const place = [overview.pickup?.city, overview.pickup?.province].filter(Boolean).join('/');
  return (
    <div className="tc-c-map-caption" role="status">
      <strong>Coleta{place ? `: ${place}` : ''}</strong>
      <span>
        Raio de {overview.radiusKm} km · {points} {points === 1 ? 'veículo' : 'veículos'} no mapa
      </span>
      {location.state === 'EXPIRED' && <span>Posição do veículo desatualizada.</span>}
      {location.state === 'MISSING' && <span>Posição válida do veículo não informada.</span>}
      {location.state === 'EXPIRED' && (
        <small>Atualize a consulta para verificar uma nova posição.</small>
      )}
      {location.state === 'UNSELECTED' && (
        <small>Selecione uma linha para centralizar o mapa no veículo.</small>
      )}
    </div>
  );
}

/**
 * Mapa do modal: coleta, círculo do raio e veículos da lista (todas as páginas). Ao selecionar
 * uma linha, centraliza na posição atual do veículo com zoom e destaca o marcador.
 */
export default function VehicleMap({ candidate, context, overview = null }) {
  const [, tick] = useState(0);
  const [failed, setFailed] = useState(false);
  const [tilesVersion, setTilesVersion] = useState(0);
  const location = vehicleLocation(candidate);
  const expires = location.position?.validUntil;
  useEffect(() => {
    if (!expires) return undefined;
    const update = () => tick((value) => value + 1);
    // Revalida no limite da fonte, inclusive após a aba ficar suspensa.
    const timer = setTimeout(
      update,
      Math.min(60000, Math.max(1, Date.parse(expires) - Date.now() + 1)),
    );
    window.addEventListener('focus', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', update);
      document.removeEventListener('visibilitychange', update);
    };
  });
  const pickup = point(overview?.pickup);
  const points = useMemo(
    () => (overview?.points || []).filter((p) => point(p)),
    [overview?.points],
  );
  const selected = location.state === 'VALID' ? point(location.position) : null;
  if (!pickup && !selected)
    return (
      <div className="tc-c-map-state" role="status">
        <MapPin size={38} aria-hidden="true" />
        <strong>{candidate ? platesLabel(candidate) : 'Nenhum veículo selecionado'}</strong>
        <span>
          {location.state === 'UNSELECTED'
            ? 'Selecione uma linha para consultar a localização.'
            : location.state === 'EXPIRED'
              ? 'Posição do veículo desatualizada.'
              : 'Posição válida do veículo não informada.'}
        </span>
        {location.state === 'EXPIRED' && (
          <small>Atualize a consulta para verificar uma nova posição.</small>
        )}
      </div>
    );
  const selectedPlate = location.state === 'VALID' ? detailText(location.vehicle.plate) : null;
  // Mesmo mapa durante a consulta: a seleção desloca o mapa em vez de recriá-lo.
  const key = JSON.stringify([context.companyId, context.item.key]);
  return (
    <>
      <Caption location={location} overview={overview || {}} points={points.length} />
      <div
        className="tc-c-leaflet"
        role="region"
        aria-label={
          selectedPlate ? `Mapa do veículo ${selectedPlate}` : 'Mapa da coleta e dos veículos'
        }
      >
        <MapContainer
          key={key}
          center={selected || pickup}
          zoom={selected ? 13 : 8}
          scrollWheelZoom={false}
        >
          <TileLayer
            key={tilesVersion}
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            eventHandlers={{ tileerror: () => setFailed(true) }}
          />
          {pickup && (
            <>
              <Circle
                center={pickup}
                radius={overview.radiusKm * 1000}
                // Raio em azul translúcido; não interativo, o arrasto e os cliques chegam ao mapa.
                interactive={false}
                className="tc-c-radius-circle"
                pathOptions={{
                  color: '#3b82f6',
                  weight: 2,
                  opacity: 0.6,
                  fill: true,
                  fillColor: '#3b82f6',
                  fillOpacity: 0.2,
                }}
              />
              <Marker
                position={pickup}
                icon={icons.pickup}
                title="Ponto de coleta"
                alt="Ponto de coleta"
              >
                <Tooltip>Coleta</Tooltip>
              </Marker>
            </>
          )}
          {points
            .filter((p) => !(selected && p.candidateId === candidate?.candidateId))
            .map((p) => (
              <Marker
                key={p.candidateId}
                position={[p.lat, p.lng]}
                icon={
                  p.evaluation === 'INCOMPLETE'
                    ? icons.incomplete
                    : p.refused
                      ? icons.refused
                      : icons.vehicle
                }
                title={`Localização de ${p.vehiclePlate || 'veículo'}`}
                alt={`Posição do veículo ${p.vehiclePlate || ''}`}
              >
                <Tooltip>{p.vehiclePlate}</Tooltip>
              </Marker>
            ))}
          {selected && (
            <Marker
              // title/alt só valem na criação do marcador: recria a cada veículo selecionado.
              key={`selected-${candidate.candidateId}`}
              position={selected}
              icon={icons.selected}
              title={`Veículo ${selectedPlate}`}
              alt={`Posição do veículo ${selectedPlate}`}
              zIndexOffset={1000}
            >
              <Tooltip permanent>{selectedPlate}</Tooltip>
            </Marker>
          )}
          <MapControls
            pickup={pickup}
            radiusKm={overview?.radiusKm ?? 0}
            points={points}
            selected={selected}
          />
        </MapContainer>
      </div>
      {failed && (
        <div className="tc-c-map-error" role="status">
          Mapa-base indisponível. A posição informada acima permanece identificada.
          <button
            type="button"
            onClick={() => {
              setFailed(false);
              setTilesVersion((v) => v + 1);
            }}
          >
            Tentar carregar mapa
          </button>
        </div>
      )}
    </>
  );
}
